"use client";

import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AccountMenuRow, AccountMenuSection } from "@/components/account/account-menu-sections";

function AccountMenuRowView({
  row,
  onNavigate,
}: {
  row: AccountMenuRow;
  onNavigate?: () => void;
}) {
  const trailing =
    row.destructive ? null : (
      <div className="flex shrink-0 items-center gap-2">
        {typeof row.badgeCount === "number" && row.badgeCount > 0 ? (
          <span className="inline-flex min-w-6 justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">
            {row.badgeCount > 99 ? "99+" : row.badgeCount}
          </span>
        ) : null}
        {row.trailing}
        <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
      </div>
    );

  const rowClassName = cn(
    "flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors",
    row.destructive
      ? "text-destructive hover:bg-destructive/10"
      : "hover:bg-accent/60 active:bg-accent",
  );

  const content = (
    <>
      <row.icon
        className={cn(
          "h-5 w-5 shrink-0",
          row.destructive ? "text-destructive" : "text-muted-foreground",
        )}
        strokeWidth={2}
      />
      <span className="min-w-0 flex-1 text-[15px] font-medium">{row.label}</span>
      {trailing}
    </>
  );

  if (row.href) {
    return (
      <Link href={row.href} className={rowClassName} onClick={onNavigate}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={rowClassName} onClick={row.onAction}>
      {content}
    </button>
  );
}

export function AccountMenuView({
  sections,
  onNavigate,
}: {
  sections: AccountMenuSection[];
  onNavigate?: () => void;
}) {
  return (
    <div className="space-y-5">
      {sections.map((section) => (
        <section key={section.key}>
          <h2 className="mb-2 px-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {section.title}
          </h2>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            {section.rows.map((row, index) => (
              <div key={row.key}>
                <AccountMenuRowView row={row} onNavigate={onNavigate} />
                {index < section.rows.length - 1 ? (
                  <div className="mx-4 border-b border-border/70" />
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
